import { Button, message, Modal, Table, Typography } from 'antd'
import { ColumnsType } from 'antd/es/table'
import { MainTitle, OrangeButton } from 'components/ui'
import { useEffect, useState } from 'react'
import { EnhancementResponse } from 'models/product'
import { productApi } from 'api/product'
import { useMemo } from 'react'
import { IMAGES_URL } from 'shared/constants/urls'
import { useNavigate, useSearchParams } from 'react-router-dom'
import noProductImage from 'assets/images/no-image-product.png'
import { showServerError } from 'shared/utils/showServerError'
import { DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons'
import { cleanObject } from 'shared/utils/cleanObject'
import qs from 'qs'
import { bodyScrollLock } from 'shared/utils/bodyScrollLock'
import { adminRoutes, superAdminRoutes } from 'shared/constants/routes'
import { useAppSelector } from 'shared/hooks/useReduxHooks'
import { appSelectors } from 'store/app'
import { ERoles } from 'models/auth'
import styles from './AiGoodsList.module.scss'

interface AiGoodsListProps {
  sellerId?: string
}

export const AiGoodsList: React.FC<AiGoodsListProps> = ({ sellerId }) => {
  const navigate = useNavigate()
  const [, setSearchParams] = useSearchParams()

  const [products, setProducts] = useState<EnhancementResponse[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  const [selectedItemsId, setSelectedItemsId] = useState<string[]>([])
  const [currentPage, setCurrentPage] = useState(1)
  const [total, setTotal] = useState(0)
  const pageSize = 10

  const role = useAppSelector(appSelectors.selectRole)
  const userSellerId = useAppSelector(appSelectors.selectSellerId)
  const isSuperAdmin = useMemo(() => role === ERoles.ADMIN, [role])

  const getAiGoods = async (page: number, isFirstLoad = false) => {
    try {
      setIsLoading(true)
      const response = await productApi.getAiGoods({
        page,
        limit: pageSize,
        sellerId: sellerId ?? userSellerId ?? ''
      })
      setProducts(response.data.items)
      setTotal(response.data.pagination.totalItems)
      if (!isFirstLoad) {
        setSearchParams(
          cleanObject({
            page: String(response.data.pagination.currentPage + 1)
          })
        )
      }
    } catch (e) {
      showServerError(e)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (window.location.search && userSellerId) {
      const params = qs.parse(window.location.search.substring(1))
      const page = Number(params.page)
      if (!isNaN(page)) {
        setCurrentPage(page)
        getAiGoods(page, true)
      }
    } else {
      getAiGoods(0, true)
    }
  }, [userSellerId])

  const deleteAiProducts = async () => {
    try {
      await productApi.deleteAiProducts(selectedItemsId)
      message.success(
        selectedItemsId.length === 1
          ? 'Товар успешно удален'
          : 'Выбранные товары успешно удалены'
      )
      if (selectedItemsId.length === products.length) {
        getAiGoods(0)
        setCurrentPage(1)
      } else {
        getAiGoods(currentPage)
      }
      setSelectedItemsId([])
    } catch (e) {
      showServerError(e)
    } finally {
      bodyScrollLock.disable()
    }
  }

  const saveAiProducts = async () => {
    try {
      setIsSaving(true)
      await productApi.saveAiProducts(selectedItemsId)
      await productApi.deleteAiProducts(selectedItemsId)
      message.success('Товары успешно сохранены')
      getAiGoods(currentPage)
    } catch (e) {
      showServerError(e)
    } finally {
      setIsSaving(false)
    }
  }

  const showDeleteConfirm = () => {
    bodyScrollLock.enable()
    Modal.confirm({
      centered: true,
      title: 'Вы действительно хотите удалить выбранные товары?',
      content: 'Отменить данное действие будет невозможно.',
      icon: <ExclamationCircleOutlined />,
      okText: 'Удалить',
      cancelText: 'Закрыть',
      onOk() {
        return new Promise((resolve) => {
          deleteAiProducts().then(resolve)
        })
      },
      onCancel() {
        bodyScrollLock.disable()
      }
    })
  }

  const onRowSelection = (selectedItemsId: React.Key[]) => {
    setSelectedItemsId(selectedItemsId as string[])
  }

  const onPageChange = (current: number) => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    getAiGoods(current)
    setCurrentPage(current)
  }

  const columns: ColumnsType<EnhancementResponse> = useMemo(
    () => [
      {
        title: 'Артикул',
        dataIndex: 'vendor_code',
        width: 101,
        render: (_, { product }) => {
          return (
            <Typography.Text>{product?.article ?? 'Не указан'}</Typography.Text>
          )
        }
      },
      {
        title: 'Фото',
        dataIndex: 'photo',
        width: 76,
        render: (_, { product }) => {
          return product.photo && !!product.photo.length ? (
            <div
              style={{
                width: 62,
                height: 34,
                backgroundImage: `url(${IMAGES_URL}${product.photo[0]})`,
                backgroundSize: 'contain',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
              }}
            />
          ) : (
            <img
              src={noProductImage}
              alt='Изображение товара'
              width={62}
              height={34}
            />
          )
        }
      },
      {
        title: 'Наименование товара',
        dataIndex: 'name',
        width: 424,
        render: (_, { product }) => {
          return <Typography.Text>{product.name}</Typography.Text>
        }
      },
      {
        title: 'Категория',
        dataIndex: 'category',
        width: 424,
        render: (_, { product }) => {
          return (
            <div style={{ display: 'flex', gap: 2 }}>
              <Typography.Text onClick={(event) => event.stopPropagation()}>
                {product.category.name + ' >'}
              </Typography.Text>
              <br />
              <Typography.Text onClick={(event) => event.stopPropagation()}>
                {product.subCategory.name}
              </Typography.Text>
            </div>
          )
        }
      }
    ],
    []
  )

  return (
    <div style={{ paddingBottom: 80 }}>
      <MainTitle text='AI-отстойник' isGoBack={!sellerId} />
      <Table
        scroll={{
          x: sellerId ? 700 : 900
        }}
        columns={columns}
        dataSource={products}
        loading={isLoading}
        rowKey={(el) => el._id}
        rowSelection={{
          selectedRowKeys: selectedItemsId,
          onChange: onRowSelection
        }}
        rowClassName='tableRow'
        onRow={(record) => ({
          onClick: () => {
            const productId = record.product._id
            navigate(
              isSuperAdmin
                ? superAdminRoutes.AI_GOODS_EDIT.replace(
                    '/:id',
                    `/${productId}`
                  )
                : adminRoutes.AI_GOODS_EDIT.replace('/:id', `/${productId}`)
            )
          }
        })}
        footer={() => (
          <div className={styles.footer}>
            <Typography.Text>Действия:</Typography.Text>
            <OrangeButton
              onClick={saveAiProducts}
              loading={isSaving}
              disabled={isSaving || !selectedItemsId.length}
            >
              Сохранить
            </OrangeButton>
            <Button
              danger
              type='primary'
              icon={<DeleteOutlined />}
              onClick={showDeleteConfirm}
              disabled={!selectedItemsId.length}
            >
              Удалить
            </Button>
          </div>
        )}
        pagination={{
          current: currentPage,
          onChange: onPageChange,
          pageSize,
          total,
          showSizeChanger: false
        }}
      />
    </div>
  )
}
